/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { EtlApiTarget } from "etl/types";

import {
	importFactoryMock,
	laboratoryFactoryMock,
	dictionaryBrandFactoryMock,
	dictionaryCategoryFactoryMock,
	dictionaryPatientFactoryMock,
	dictionaryProductFactoryMock,
	dictionaryScheduleTypeFactoryMock,
	dictionarySupplierFactoryMock,
	dictionaryUserFactoryMock,
	extractBrandFactoryMock,
	extractCategoryFactoryMock,
	extractPatientFactoryMock,
	extractProductFactoryMock,
	extractScheduleTypeFactoryMock,
	extractSupplierFactoryMock,
	extractUserFactoryMock,
} from "etl/factories";
import { sleep } from "etl/utils";
import { EntityId } from "@reduxjs/toolkit";

const addIds = <T extends { id: number; "@id": string }>(data: Partial<T>, entities: T[], target: string): T => {
	if (!entities) {
		return {
			id: 1,
			"@id": `${target}/${1}`,
			...data,
		} as T;
	}
	const maxIndex = entities.reduce((acc, entity) => (entity.id > acc ? entity.id : acc), 0);
	return {
		id: maxIndex + 1,
		"@id": `${target}/${maxIndex + 1}`,
		...data,
	} as T;
};

/**
 * Load all fake data in object
 */
const repository = {
	imports: importFactoryMock(7),
	laboratories: laboratoryFactoryMock(5),
	extractpatients: extractPatientFactoryMock(3),
	extractusers: extractUserFactoryMock(3),
	extractbrands: extractBrandFactoryMock(3),
	extractsuppliers: extractSupplierFactoryMock(3),
	extractcategories: extractCategoryFactoryMock(3),
	extractproducts: extractProductFactoryMock(3),
	extractscheduletypes: extractScheduleTypeFactoryMock(3),
	dictionarypatients: dictionaryPatientFactoryMock(3),
	dictionaryusers: dictionaryUserFactoryMock(3),
	dictionarybrands: dictionaryBrandFactoryMock(3),
	dictionarysuppliers: dictionarySupplierFactoryMock(3),
	dictionarycategories: dictionaryCategoryFactoryMock(3),
	dictionaryproducts: dictionaryProductFactoryMock(3),
	dictionaryscheduletypes: dictionaryScheduleTypeFactoryMock(3),
};

const factories = {
	imports: importFactoryMock,
	laboratories: laboratoryFactoryMock,
	extractpatients: extractPatientFactoryMock,
	extractusers: extractUserFactoryMock,
	extractbrands: extractBrandFactoryMock,
	extractsuppliers: extractSupplierFactoryMock,
	extractcategories: extractCategoryFactoryMock,
	extractproducts: extractProductFactoryMock,
	extractscheduleTypes: extractScheduleTypeFactoryMock,
	dictionarypatients: dictionaryPatientFactoryMock,
	dictionaryusers: dictionaryUserFactoryMock,
	dictionarybrands: dictionaryBrandFactoryMock,
	dictionarysuppliers: dictionarySupplierFactoryMock,
	dictionarycategories: dictionaryCategoryFactoryMock,
	dictionaryproducts: dictionaryProductFactoryMock,
	dictionaryscheduletypes: dictionaryScheduleTypeFactoryMock,
};

const APIWithRepository: Record<EtlApiTarget, keyof typeof repository | ""> = {
	[EtlApiTarget.ETL_IMPORTS_API]: "imports",
	[EtlApiTarget.LABORATORIES_API]: "laboratories",
	[EtlApiTarget.EXTRACT_BRANDS_API]: "extractbrands",
	[EtlApiTarget.EXTRACT_CATEGORIES_API]: "extractcategories",
	[EtlApiTarget.EXTRACT_PATIENTS_API]: "extractpatients",
	[EtlApiTarget.EXTRACT_PRODUCTS_API]: "extractproducts",
	[EtlApiTarget.EXTRACT_SCHEDULE_TYPES_API]: "extractscheduletypes",
	[EtlApiTarget.EXTRACT_SUPPLIERS_API]: "extractsuppliers",
	[EtlApiTarget.EXTRACT_USERS_API]: "extractusers",
	[EtlApiTarget.DICTIONARY_BRANDS_API]: "dictionarybrands",
	[EtlApiTarget.DICTIONARY_CATEGORIES_API]: "dictionarycategories",
	[EtlApiTarget.DICTIONARY_PATIENTS_API]: "dictionarypatients",
	[EtlApiTarget.DICTIONARY_PRODUCTS_API]: "dictionaryproducts",
	[EtlApiTarget.DICTIONARY_SCHEDULE_TYPES_API]: "dictionaryscheduletypes",
	[EtlApiTarget.DICTIONARY_SUPPLIERS_API]: "dictionarysuppliers",
	[EtlApiTarget.DICTIONARY_USERS_API]: "dictionaryusers",
};

type RepositoryKeys = keyof typeof repository;

/**
 * Get repository by target
 */
export const getRepositoryByTarget = (target: EtlApiTarget): any => repository[APIWithRepository[target] as keyof typeof repository] ?? [];

/**
 * Get factories by target
 */
export const getFactoryByTarget = (target: EtlApiTarget) => factories[APIWithRepository[target] as keyof typeof repository] ?? [];

/**
 * Setter repository by target
 */
const setRepositoryByTarget = (target: EtlApiTarget, data: any) => {
	if (!repository[APIWithRepository[target] as RepositoryKeys]) return;
	repository[APIWithRepository[target] as RepositoryKeys] = data;
};

export const inMemoryRepository = {

	async find<T extends { id: number }>(target: EtlApiTarget, id: EntityId) {
		const entities = getRepositoryByTarget(target) as T[];
		return entities.find((entity) => entity.id === id);
	},

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	async findAll<T>(target: EtlApiTarget, condition?: string, raw?: boolean) {
		const entities = getRepositoryByTarget(target) as T[];
		if (raw)
			return {
				"hydra:totalItems": entities.length,
				"hydra:member": entities,
				"hydra:view": {
					"hydra:next": `/api/${target}?page=1`,
					"hydra:last": `/api/${target}?page=1`,
				},
			};

		return entities;
	},

	async update<T extends { id?: number }>(target: EtlApiTarget, id: string | number, data: Partial<T>) {

		// fake validate ETL imports selection
		if ([
			EtlApiTarget.LOAD_DICTIONARY_ETL,
			EtlApiTarget.LOAD_EXTRACT_ETL,
			EtlApiTarget.LOAD_IMPORT_ETL,
			EtlApiTarget.LOAD_MAPPING_ETL
		].includes(target)) {
			// fast for test
			if (import.meta.env.MODE === "test") return { data: { success: true } };
			// slow browser in mock mode
			await sleep(2000);
			return { data: { success: true } }
		}

		// slow browser for update call in mock mode
		if (import.meta.env.MODE !== "test") {
			await sleep(500);
		}

		const cleanId = parseInt(id.toString().replace(`${target}/`, ""), 10);

		let entities = getRepositoryByTarget(target) as T[];

		entities = entities.map((entity) => {
			if (entity.id && entity.id === cleanId) {
				return {
					...entity,
					...(data as typeof entity),
				};
			}

			return entity;
		});

		setRepositoryByTarget(target, entities);

		return { data: entities?.find((entity: T) => entity.id === cleanId) };
	},

	async create<T>(target: EtlApiTarget, data: Partial<T>) {
		const entities = getRepositoryByTarget(target);
		let newEntity = addIds(data, entities, target);
		newEntity = { ...getFactoryByTarget(target)(1)[0], ...newEntity };
		setRepositoryByTarget(target, [...entities, newEntity]);
		return { data: newEntity };
	},

	async trigger(target: EtlApiTarget): Promise<any> {
		return { "status": "success", "message": "Processed successfully" };
	}
}

export default inMemoryRepository;
